import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import ReactDOMServer from 'react-dom/server';

export default function LineChart({ children, data = null, labelX, labelY }) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (data === null) {
      return;
    }

    const config = {
      type: 'line',
      data: data,
      options: {
        responsive: true,
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            external: tooltipCallback,
          },
        },
        interaction: {
          intersect: false,
        },
        pointStyle: true,
        elements: {
          point: {
            backgroundColor: 'transparent',
            hoverBackgroundColor: '#06315D',
            borderWidth: 0,
            hoverBorderWidth: 0,
            radius: 5,
            hoverRadius: 5,
          },
          line: {
            borderCapStyle: 'round',
            borderWidth: 4,
          },
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: true,
              drawTicks: true,
              color: 'transparent',
              tickColor: '#D0D9E0',
              tickLength: 4,
              z: 10,
            },
            border: {
              display: true,
              color: '#D0D9E0',
              z: 10,
            },
            title: {
              display: false,
            },

            ticks: {
              color: '#6A839E',
              font: {
                size: '10px',
                family: 'Lato, sans-serif',
              },
              padding: 8,
            },
          },
          y: {
            display: true,
            grid: {
              color: '#e1e7eb',
              drawTicks: false,
            },
            border: {
              display: false,
            },
            title: {
              display: false,
            },
            ticks: {
              color: '#6A839E',
              font: {
                size: '10px',
                family: 'Lato, sans-serif',
              },
              padding: 5,
              callback: function (tick) {
                return tick === 0 ? undefined : Math.round(tick * 10) / 10;
              },
            },
            suggestedMin: 0,
            suggestedMax: 5,
          },
        },
      },
    };

    const ctx = chartRef.current.getContext('2d');
    const existingChart = chartRef.current.chart;

    if (existingChart) {
      existingChart.destroy();
    }

    chartRef.current.chart = new Chart(ctx, config);
  }, [data]);

  return (
    <div
      data-component-name='LineChart'
      className='relative flex flex-col pt-6 pb-6 pl-4 pr-5 text-xs text-blue-300 rounded-lg bg-blue-50'
    >
      {labelY && labelY}
      <canvas
        height='326px'
        width='300px'
        className='w-full h-full mt-2'
        ref={chartRef}
      ></canvas>
      {labelX && labelX}
      {children && <div className='mt-5 flex w-full'>{children}</div>}
    </div>
  );
}

let tooltipCallback = function (context) {
  // Tooltip Element
  let tooltipEl = document.getElementById('chartjs-tooltip');

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chartjs-tooltip';
    document.body.appendChild(tooltipEl);
  }

  // Hide if no tooltip
  const tooltipModel = context.tooltip;
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set caret Position
  tooltipEl.classList.remove('above', 'below', 'no-transform');
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  } else {
    tooltipEl.classList.add('no-transform');
  }

  if (tooltipModel.body) {
    let currentValue = context?.tooltip?.body?.[0]?.lines?.[0] || null;
    let currentValueParsed = parseFloat(currentValue.replace(',', '.')).toFixed(1);

    tooltipEl.innerHTML = ReactDOMServer.renderToStaticMarkup(
      <div className='h-8 relative text-sm font-bold -translate-x-1/2 -translate-y-[54px] drop-shadow-tooltip'>
        <div className='relative z-20 flex items-center w-full h-full px-4 border rounded-md bg-white-400 border-blue-50'>
          <div className='text-blue-500'>{currentValueParsed}</div>
        </div>

        <div className='w-4 rotate-45 bg-blue-50 absolute -bottom-[4px] left-1/2 -translate-x-1/2 h-4 z-10'></div>
        <div className='w-4 border-t-white-400 border-8 absolute -bottom-[14px] left-1/2 -translate-x-1/2 h-4 z-20 border-white-400/0'></div>
      </div>
    );
  }

  const position = context.chart.canvas.getBoundingClientRect();

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.position = 'absolute';
  tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
  tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
  tooltipEl.style.padding = tooltipModel.padding + 'px ' + tooltipModel.padding + 'px';
  tooltipEl.style.pointerEvents = 'none';
};
